import { useMemo } from "react";
import { Link } from "react-router-dom";
import isAuthenticated from "../../components/Utils/isAuthenticated";
import { getNavGroupRoutes, getNavGroupExternalLinks } from "../../config/routes";
import spotgraphic from "../../images/spot-graphic2022.png";
import Footer from "../RFS/components/Footer";

export default function SpotSurveysPage() {
	const auth = useMemo(() => isAuthenticated(), []);
	const userAccess = useMemo(
		() =>
			auth.access
				? auth.access
						.split(",")
						.map((a) => a.trim())
						.filter(Boolean)
				: [],
		[auth.access],
	);

	const routes = useMemo(() => getNavGroupRoutes("spot", userAccess), [userAccess]);
	const externalLinks = useMemo(() => getNavGroupExternalLinks("spot", userAccess), [userAccess]);

	const hasOptions = routes.length > 0 || externalLinks.length > 0;

	const steps = [
		{
			num: 1,
			text: "Prior to the start of the feedback period, instructors will receive an email announcement with information and important dates for the survey period.",
		},
		{
			num: 2,
			text: "Once they have received the announcement, instructors will notify their students about the upcoming SPOT surveys to encourage participation.",
		},
		{
			num: 3,
			text: "When the SPOT surveys become available, students will receive emails with instructions and the appropriate links to complete their feedback.",
		},
		{
			num: 4,
			text: "Students will have until the end of the survey period to submit their responses. Surveys can be completed on any device with internet access, can be put down and resumed at any time, and are anonymous.",
		},
		{
			num: 5,
			text: "If students have not completed their SPOT surveys, they will periodically receive reminders via email, with a final reminder being delivered on the last day of the survey window.",
		},
		{
			num: 6,
			text: "Once the SPOT surveys have concluded and grades are finalized, instructors and students will be able to view survey results online through the SPOT Survey Report.",
		},
	];

	return (
		<div className="bg-digitalWhite min-h-[78svh] flex flex-col ">
			{/* Hero */}
			<div className="  w-full mx-auto  px-6 md:px-14">
				<div className="grid grid-cols-3  gap-x-8 py-6 md:py-12">
					<div className="flex md:flex-row flex-col items-center gap-10 px-4 col-span-2  bg-white rounded-xl shadow-sm">
						<div className="w-1/2  md:order-1 order-2 flex items-center">
							<img
								alt="SPOT"
								src={spotgraphic}
								loading="eager"
								className="h-auto w-full rounded-2xl shadow-md"
							/>
						</div>
						<div className="flex flex-col md:w-1/2 w-full md:order-2 order-1 gap-4">
							<h1 className="text-4xl md:text-5xl font-normal leading-tight">
								Collect feedback that counts
							</h1>
							<p className="text-lg text-gray-600 leading-relaxed">
								Student Perspectives of Teaching (SPOT) gather feedback from classes to help
								instructors, departments and faculties improve curriculum and instruction. The
								collection of SPOT is regulated by University of Alberta Policies and Procedures (
								<a
									href="https://policiesonline.ualberta.ca/PoliciesProcedures/Procedures/Student-Input-to-the%20Evaluation-of-Teaching-and-Learning-Procedure.pdf"
									target="_blank"
									rel="noreferrer"
									className="text-primary underline hover:text-primary/80 transition-colors"
								>
									UAPPOL
								</a>
								). The current version of this policy may be viewed at the Teaching, Learning and
								Evaluation Policy.
							</p>
						</div>
					</div>
					{/* Navigation cards */}
					<div className="w-full max-w-xl mx-auto px-6 md:px-10 py-8 bg-white rounded-xl shadow-sm">
						<div className="mb-6">
							<h2 className="text-2xl font-semibold text-primary">SPOT Surveys</h2>
							<p className="text-gray-600 mt-1">Choose a SPOT destination below.</p>
						</div>

						{!hasOptions && (
							<div className="rounded-lg border border-gray-200 bg-gray-50 p-5 text-gray-600">
								No SPOT survey options available for your account.
							</div>
						)}

						{routes.length > 0 && (
							<div className="mb-6">
								<h3 className="text-base font-semibold text-gray-500 uppercase tracking-wide mb-3">
									Internal Pages
								</h3>
								<ul className="grid gap-3 sm:grid-cols-1">
									{routes.map((route) => (
										<li key={route.path}>
											<Link
												to={route.path}
												className="block rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition hover:border-primary hover:shadow-md"
											>
												<span className="text-primary font-semibold">{route.label}</span>
											</Link>
										</li>
									))}
								</ul>
							</div>
						)}

						{externalLinks.length > 0 && (
							<div>
								<h3 className="text-base font-semibold text-gray-500 uppercase tracking-wide mb-3">
									External Links
								</h3>
								<ul className="grid gap-3 sm:grid-cols-2">
									{externalLinks.map((link) => (
										<li key={link.url}>
											<a
												href={link.url}
												target="_blank"
												rel="noreferrer"
												className="block rounded-lg border border-gray-200 bg-white p-5 shadow-sm transition hover:border-primary hover:shadow-md"
											>
												<span className="text-primary font-semibold">{link.label}</span>
												<span className="block text-xs text-gray-500 mt-1">
													Opens in a new tab ↗
												</span>
											</a>
										</li>
									))}
								</ul>
							</div>
						)}
					</div>
				</div>
			</div>
			{/* SPOT Process */}
			<div className="flex flex-col items-center bg-primary py-16 px-6 md:px-20">
				<h2 className="text-4xl md:text-5xl font-light text-white mb-8">SPOT Process</h2>
				<div className="grid md:grid-cols-3 grid-cols-1 gap-6 max-w-5xl w-full">
					{steps.map((step) => (
						<div
							key={step.num}
							className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
						>
							<span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white text-primary text-sm font-bold mb-3">
								{step.num}
							</span>
							<p className="text-white/90 leading-relaxed text-sm">{step.text}</p>
						</div>
					))}
				</div>
			</div>

			<Footer />
		</div>
	);
}
